import { computed, ComputedRef, Ref } from "vue";
import { BaseInputAffix } from "@/src/design_system/form_elements/base_input/types";
import type { DSBaseInputProps } from "@/src/design_system/form_elements/base_input/types/dsBaseInput";
import type { DSSelectProps } from "@/src/design_system/form_elements/select/types/props";
import type { DSBaseListOption } from "@/src/design_system/form_elements/option_list/types";

interface UseSelectInputPropsArgs {
  props: ComputedRef<Readonly<DSSelectProps>>;
  suffix: ComputedRef<BaseInputAffix>;
  showSelectedTags: ComputedRef<boolean>;
  selectedOptions: ComputedRef<DSBaseListOption[]>;
  listId: string;
  isOpen: Ref<boolean>;
  activeOptionId: Ref<string | undefined>;
}

/** An option only becomes the closed control's prefix when it carries something renderable. */
const hasAffixContent = (option?: DSBaseListOption): boolean => {
  if (!option) return false;

  return Boolean(option.flag || option.icon || option.avatarUrl);
};

export const useSelectInputProps = ({
  props,
  suffix,
  showSelectedTags,
  selectedOptions,
  listId,
  isOpen,
  activeOptionId,
}: UseSelectInputPropsArgs): ComputedRef<DSBaseInputProps> => {
  const computedPrefix = computed<BaseInputAffix | undefined>(() => {
    if (props.value.prefix) {
      return props.value.prefix;
    }

    if (props.value.multiple) {
      return undefined;
    }

    const selectedOption = selectedOptions.value[0];

    return hasAffixContent(selectedOption) ? selectedOption : undefined;
  });

  return computed<DSBaseInputProps>(() => {
    const isAutocomplete = props.value.autocomplete;

    return {
      dataCySuffix: props.value.name,
      type: isAutocomplete ? "search" : "text",
      placeholder: props.value.placeholder,
      inlineEdit: props.value.inlineEdit,
      disabled: props.value.disabled,
      readonly: !isAutocomplete,
      required: props.value.required,
      hasError: !!props.value.errorMessages,
      prefix: computedPrefix.value,
      suffix: suffix.value,
      autocomplete: "off",
      hiddenInput: isAutocomplete ? false : showSelectedTags.value,
      clearable: props.value.clearable,
      forceShowClear: props.value.multiple && selectedOptions.value.length > 0,
      cursorPointer: !isAutocomplete,
      wrapContent: props.value.multiple,
      role: "combobox",
      ariaControls: listId,
      ariaExpanded: isOpen.value,
      ariaActivedescendant: activeOptionId.value,
      ariaAutocomplete: isAutocomplete ? "list" : "none",
    };
  });
};
